function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import map from 'lodash/map';
import unionBy from 'lodash/unionBy';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import { useAction } from 'hooks';
import Alert from 'components/Alert';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { transformSystemAlerts } from './helpers';
/* eslint-disable @typescript-eslint/typedef */ // TODO: Define types
const onAction = (action, dispatch)=>{
    if (isFunction(action)) {
        action();
    } else if (isObject(action)) {
        dispatch(action);
    }
};
const onClose = (alert, state, setState, dispatch)=>{
    const { id, alertConfigurationId } = alert;
    if (alertConfigurationId) {
        setState(_object_spread_props(_object_spread({}, state), {
            systemAlerts: state.systemAlerts.filter((a)=>a.id !== id)
        }));
        dispatch(actions.dismissSystemAlert(alert));
    } else {
        setState(_object_spread_props(_object_spread({}, state), {
            alerts: state.alerts.filter((a)=>a.id !== id)
        }));
        if (alert.attributes && alert.attributes.isPinned) {
            dispatch(actions.dismissAlert(alert));
        }
    }
};
const setAlertsEffect = (alerts, state, setState, isGlobal, onDisplayAlerts)=>{
    const alertsTimeThreshold = useSelector(selectors.alertsTimeThreshold);
    const dismissedAlerts = useSelector(selectors.dismissedAlerts);
    const openModalId = useSelector(selectors.modal);
    const getAlertName = (alert)=>isObject(alert.message) ? `${alert.message.id}_${JSON.stringify(alert.messageValues)}` : alert.message;
    useEffect(()=>{
        if (alerts.length || alertsTimeThreshold > state.alertsTimeThreshold) {
            const localAlerts = filter(alerts, (alert)=>{
                const alertName = getAlertName(alert);
                return !includes(dismissedAlerts, alertName) && (!openModalId || openModalId && (isGlobal && alert.isGlobal || !isGlobal && !alert.isGlobal));
            });
            let unionAlerts = unionBy([
                ...state.alerts
            ].reverse(), localAlerts, (alert)=>getAlertName(alert));
            unionAlerts = filter(unionAlerts, (alert)=>alert.timestamp >= alertsTimeThreshold).reverse();
            setState(_object_spread_props(_object_spread({}, state), {
                alerts: unionAlerts,
                alertsTimeThreshold,
                isVisible: true
            }));
            onDisplayAlerts();
        }
    }, [
        alerts,
        alertsTimeThreshold
    ]);
};
const setSystemAlertsEffect = (systemAlerts, state, setState, isGlobal, onDisplayAlerts)=>{
    if (!isGlobal) {
        return;
    }
    const systemAlertsSettings = useSelector(selectors.systemAlertsSettings);
    const localizationResources = useSelector(selectors.localizationResources);
    useEffect(()=>{
        if (systemAlerts.length) {
            let unionSystemAlerts = [];
            unionSystemAlerts = unionBy([
                ...state.systemAlerts
            ].reverse(), systemAlerts, (systemAlert)=>`${systemAlert.alertId}_${systemAlert.createTimestamp}`);
            if (unionSystemAlerts.length <= state.systemAlerts.length) {
                return;
            }
            unionSystemAlerts = transformSystemAlerts(unionSystemAlerts, systemAlertsSettings, localizationResources).filter((systemAlert)=>systemAlert && systemAlert.message).reverse();
            setState(_object_spread_props(_object_spread({}, state), {
                systemAlerts: unionSystemAlerts,
                isVisible: true
            }));
            onDisplayAlerts();
        }
    }, [
        systemAlerts
    ]);
};
const AlertsBus = ({ className, isGlobal = false })=>{
    if (!process.env.BROWSER) {
        return null;
    }
    const dispatch = useDispatch();
    const alerts = useSelector(selectors.alerts);
    const systemAlerts = useSelector(selectors.systemAlerts);
    const route = useSelector(selectors.route);
    const prevRouteRef = useRef(null);
    const [state, setState] = useState({
        alerts: [],
        systemAlerts: [],
        alertsTimeThreshold: +moment.utc().locale('en').format('X'),
        activeClinicMembership: null,
        isVisible: false
    });
    const onDisplayAlerts = debounce(useAction(actions.displayAlerts), 150);
    setAlertsEffect(alerts, state, setState, isGlobal, onDisplayAlerts);
    setSystemAlertsEffect(systemAlerts, state, setState, isGlobal, onDisplayAlerts);
    useEffect(()=>{
        if (!route.previousName || prevRouteRef.current === route) {
            return;
        }
        setState(_object_spread_props(_object_spread({}, state), {
            alerts: filter(state.alerts, (alert)=>alert.attributes && alert.attributes.isPinned)
        }));
        prevRouteRef.current = route;
    }, [
        route
    ]);
    const allAlerts = [
        ...state.alerts,
        ...state.systemAlerts
    ];
    return /*#__PURE__*/ _jsx(motion.div, {
        initial: {
            height: 0
        },
        animate: {
            height: allAlerts.length ? 'auto' : 0
        },
        transition: {
            ease: 'easeIn',
            duration: allAlerts.length ? 0.1 : 0.3
        },
        style: {
            overflow: 'hidden'
        },
        children: /*#__PURE__*/ _jsx("div", {
            className: cn('alertsBus', className),
            children: /*#__PURE__*/ _jsx(AnimatePresence, {
                children: map(allAlerts, (alert)=>/*#__PURE__*/ _jsx(motion.div, {
                        initial: {
                            opacity: 0,
                            height: 0
                        },
                        animate: {
                            opacity: 1,
                            height: 'auto'
                        },
                        exit: {
                            opacity: 0,
                            height: 0
                        },
                        transition: {
                            ease: 'linear',
                            duration: 0.3
                        },
                        children: /*#__PURE__*/ _jsx(Alert, _object_spread_props(_object_spread({}, alert), {
                            actions: alert.actions && alert.actions.map((action)=>_object_spread_props(_object_spread({}, action), {
                                    action: ()=>onAction(action.action, dispatch)
                                })),
                            onClose: ()=>onClose(alert, state, setState, dispatch)
                        }))
                    }, `alert-${isObject(alert.message) ? alert.message.id : alert.message}-${alert.id}`))
            })
        })
    });
};
export default AlertsBus;
